import { useCallback, useRef } from 'react'
import { clampSize } from './activitiesLayout'

const STEP = 24

function Splitter({ axis, tableSize, panesSize, onSizeChange, onReset, labelPrefix }) {
    const ref = useRef(null)
    const dragRef = useRef(null)
    const frameRef = useRef(null)
    const lastSizeRef = useRef(tableSize)
    const sizeRef = useRef(tableSize)
    sizeRef.current = tableSize

    const isX = axis === 'x'
    const orientation = isX ? 'vertical' : 'horizontal'

    const handlePointerDown = useCallback((e) => {
        if (e.button !== 0) return
        dragRef.current = {
            startPos: isX ? e.clientX : e.clientY,
            startSize: sizeRef.current,
        }
        if (ref.current.setPointerCapture) {
            try { ref.current.setPointerCapture(e.pointerId) } catch (err) { /* ignore */ }
        }
        ref.current.classList.add('is-dragging')
        document.body.classList.add('splitter-dragging')
        document.body.classList.add(`splitter-dragging-${axis}`)
    }, [isX, axis])

    const handlePointerMove = useCallback((e) => {
        const drag = dragRef.current
        if (!drag) return
        const pos = isX ? e.clientX : e.clientY
        const size = clampSize(drag.startSize + (pos - drag.startPos), panesSize)
        lastSizeRef.current = size
        if (frameRef.current) cancelAnimationFrame(frameRef.current)

        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null
            onSizeChange(size, false)
        })
    }, [isX, panesSize, onSizeChange])

    const endDrag = useCallback((e) => {
        if (!dragRef.current) return
        if (ref.current.releasePointerCapture && e.pointerId != null) {
            try { ref.current.releasePointerCapture(e.pointerId) } catch (err) { /* ignore */ }
        }
        ref.current.classList.remove('is-dragging')
        document.body.classList.remove('splitter-dragging')
        document.body.classList.remove(`splitter-dragging-${axis}`)
        dragRef.current = null
        if (frameRef.current) { cancelAnimationFrame(frameRef.current); frameRef.current = null }
        onSizeChange(lastSizeRef.current, true)
    }, [axis, onSizeChange])

    const handleKeyDown = useCallback((e) => {
        let delta = 0
        if (isX) {
            if (e.key === 'ArrowLeft') delta = -STEP
            else if (e.key === 'ArrowRight') delta = STEP
        } else {
            if (e.key === 'ArrowUp') delta = -STEP
            else if (e.key === 'ArrowDown') delta = STEP
        }
        if (delta === 0) return
        e.preventDefault()
        onSizeChange(clampSize(sizeRef.current + delta, panesSize), true)
    }, [isX, panesSize, onSizeChange])

    return (
        <div
            ref={ref}
            className="splitter"
            role="separator"
            tabIndex={0}
            aria-orientation={orientation}
            aria-label={labelPrefix ? `${labelPrefix}: таблица / детали` : undefined}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onDoubleClick={onReset}
            onKeyDown={handleKeyDown}
        />
    )
}

export default Splitter